/**
 * One cross-section -> one crest point, or a rejection.
 *
 * The ordering here is load-bearing and was arrived at by measurement (spec
 * §8): contiguity gate, then top region BY SLOPE, then flat/relief
 * classification on the top's relief, then the tie-break, then the
 * corridor-wall rejection. Reordering these reintroduces defects the spec
 * documents.
 */
import type { DrapeSampler } from '@/core/alignment';

import type { CrestParams, GuideStation, SectionKind } from './types';

export interface SectionPick {
  x: number;
  y: number;
  z: number;
  offset: number;
  kind: SectionKind;
}

function median(sorted: number[]): number {
  const n = sorted.length;
  if (n === 0) return 0;
  const mid = n >> 1;
  return n % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

export function scanSection(
  st: GuideStation,
  sampler: DrapeSampler,
  params: CrestParams,
  prevOffset: number | null,
): SectionPick | null {
  const { corridorHalfWidth: W, offsetStep: dO, topSlopeThreshold, flatTolerance, tieTolerance } = params;
  if (!(W > 0) || !(dO > 0)) return null;

  // Left-hand normal: offset is negative to the left, looking up-station.
  const nx = -st.ty;
  const ny = st.tx;

  const n = Math.floor((2 * W) / dO) + 1;
  const zeroIdx = Math.round(W / dO);
  const off = new Float64Array(n);
  const z = new Float64Array(n);
  const ok = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const o = -W + i * dO;
    off[i] = o;
    const v = sampler(st.x + o * nx, st.y + o * ny);
    if (v !== null && Number.isFinite(v)) {
      z[i] = v;
      ok[i] = 1;
    }
  }

  // Early-out: the guide itself has no surface under it, so there is nothing
  // to scan. This is a COST guard, not a correctness one — every case it
  // catches is also caught downstream (a wide hole by the min-sample gate
  // below, a single-cell hole by the final re-sample of the chosen offset).
  // Keep it: gapped stations are common on real surfaces and this skips the
  // entire section for them. Do not expect a test to fail if you remove it.
  if (!ok[zeroIdx]) return null;
  let lo = zeroIdx;
  while (lo > 0 && ok[lo - 1]) lo--;
  let hi = zeroIdx;
  while (hi < n - 1 && ok[hi + 1]) hi++;
  if (hi - lo + 1 < 5) return null;

  // --- Top region by SLOPE. Side slopes run at ~0.67 and longitudinal-grade
  // contamination at ~0.01, a 60x separation, so this boundary survives a
  // skewed section where an elevation-based test does not (spec §7.1 step 3).
  const slopeAt = (i: number): number => {
    const a = Math.max(i - 1, lo);
    const b = Math.min(i + 1, hi);
    const do_ = off[b] - off[a];
    return do_ === 0 ? Infinity : Math.abs((z[b] - z[a]) / do_);
  };
  if (!(slopeAt(zeroIdx) < topSlopeThreshold)) return null;
  let a = zeroIdx;
  while (a > lo && slopeAt(a - 1) < topSlopeThreshold) a--;
  let b = zeroIdx;
  while (b < hi && slopeAt(b + 1) < topSlopeThreshold) b++;

  let topMin = Infinity;
  let topMax = -Infinity;
  for (let i = a; i <= b; i++) {
    if (z[i] < topMin) topMin = z[i];
    if (z[i] > topMax) topMax = z[i];
  }

  let pick: number;
  let kind: SectionKind;
  if (topMax - topMin <= flatTolerance) {
    // FLAT: no meaningful high point exists. The crest is the top's centre.
    pick = (off[a] + off[b]) / 2;
    kind = 'flat';
  } else {
    // RELIEF: argmax over the WHOLE valid run, deliberately not restricted to
    // the top region — the top boundary is only located to within one
    // offsetStep, which costs ~240 mm on a crossfall (spec §7.1 step 4).
    let zmax = -Infinity;
    for (let i = lo; i <= hi; i++) if (z[i] > zmax) zmax = z[i];
    const tied: number[] = [];
    for (let i = lo; i <= hi; i++) if (z[i] >= zmax - tieTolerance) tied.push(off[i]);
    const tMin = tied[0];
    const tMax = tied[tied.length - 1];
    if (prevOffset !== null && tMin <= prevOffset && prevOffset <= tMax) {
      let best = tied[0];
      let bestD = Infinity;
      for (const o of tied) {
        const d = Math.abs(o - prevOffset);
        if (d < bestD) {
          bestD = d;
          best = o;
        }
      }
      pick = best;
    } else {
      pick = median(tied);
    }
    kind = 'relief';
  }

  // --- Corridor-wall rejection: a maximum on the wall means the corridor is
  // mis-set or the section is clipped, not that the crest is there.
  if (pick <= off[lo] + dO || pick >= off[hi] - dO) return null;

  const px = st.x + pick * nx;
  const py = st.y + pick * ny;
  const pz = sampler(px, py);
  if (pz === null || !Number.isFinite(pz)) return null;
  return { x: px, y: py, z: pz, offset: pick, kind };
}
